import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useUserData } from "src/context/userContext";
import { asiakas_fetchList } from "src/data/asiakas/AsiakasFunctions";
import ProgressCirc from "src/components/layout/Progress/ProgressCirc";
import MyDialog from "src/components/MyDialog/MyDialog";
import EditAsiakasNew from "../edit/components/EditAsiakasNew";

export default function AsiakasSelectorAutocomplete({
  height = undefined,
  shrinkLabel = false,
  onBlur = () => {},
  ownerAsiakasId = 0,
  ...props
}) {
  const variant = props.variant || "standard"; // filled, outlined, standard
  const { token, userAsiakas } = useUserData();
  if (ownerAsiakasId === 0) ownerAsiakasId = userAsiakas?.asiakasId || 0;
  const [asiakasList, setAsiakasList] = useState(null);
  const [selectedAsiakasId, setSelectedAsiakasId] = useState(props.defaultAsiakasId || 0);
  const [uusiAsiakasIsOpen, setUusiAsiakasIsOpen] = useState(false);
  const [newAsiakasNimi, setNewAsiakasNimi] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [inputValue, setInputValue] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isEditing, setIsEditing] = useState(false);
  const label = props.label || "Asiakashaku";

  let filter = props.filter || "tyomaa";
  // betoni, pumppu, toimittajat, default: tyomaa
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetchAsiakasListFunc(filter).then((asiakasList) => {
      setAsiakasList(asiakasList);
      setIsLoading(false);
    });
  }, [filter]);

  useEffect(() => {
    if (props.defaultAsiakasId !== selectedAsiakasId)
      setSelectedAsiakasId(props.defaultAsiakasId || 0);
  }, [props.defaultAsiakasId]);

  const fetchAsiakasListFunc = async (filter) => {
    const asiakasList = await asiakas_fetchList(-1, -1, filter, token, ownerAsiakasId);
    return asiakasList;
  };

  const sortedAsiakasList = () => {
    if (!asiakasList) return [];
    return asiakasList.sort((a, b) => {
      if (a.asiakasNimi < b.asiakasNimi) return -1;
      if (a.asiakasNimi > b.asiakasNimi) return 1;
      return 0;
    });
  };

  const getAsiakas = (asiakasId) => {
    if (!asiakasId) return 0;
    if (!asiakasList) return 0;
    const _selectedAsiakas = asiakasList.find((asiakas) => asiakas.asiakasId === asiakasId);
    return _selectedAsiakas;
  };

  let handleAutoCompleteSelect = (asiakas) => {
    if (!asiakas) {
      setSelectedAsiakasId(0);
      props.setAsiakas && props.setAsiakas(null);
      return;
    }
    if (asiakas.asiakasId === 0) {
      setNewAsiakasNimi(asiakas.ytunnus);
      setUusiAsiakasIsOpen(true);
      return;
    }
    setSelectedAsiakasId(asiakas.asiakasId);
    if (asiakas.asiakasId === 0) {
      asiakas.asiakasNimi = asiakas.ytunnus;
      return props.setAsiakas && props.setAsiakas(asiakas);
    } else {
      const _asiakas = getAsiakas(asiakas.asiakasId);
      props.setAsiakas && props.setAsiakas(_asiakas);
    }
  };

  const onInputChange = (inputValue) => {
    setInputValue(inputValue);
  };

  const onOpen = () => {
    setIsEditing(true);
    props.setIsEditing && props.setIsEditing(true);
  };

  const onClose = () => {
    setIsEditing(false);
    props.setIsEditing && props.setIsEditing(false);
  };

  const handleEditAsiakasSave = async (asiakas) => {
    // update asiakasList to include the new asiakas
    const asiakasList = await fetchAsiakasListFunc(filter);
    setAsiakasList(asiakasList);
    handleAutoCompleteSelect(asiakas);
    setUusiAsiakasIsOpen(false);
  };

  const handleEditAsiakasCancel = () => {
    setUusiAsiakasIsOpen(false);
    handleAutoCompleteSelect(null);
    setInputValue("");
    setSelectedAsiakasId(null);
    return true;
  };

  return (
    <>
      <MyDialog title="Uusi asiakas" open={uusiAsiakasIsOpen} onCancel={handleEditAsiakasCancel}>
        <EditAsiakasNew
          asiakasNimi={newAsiakasNimi}
          yTunnus=""
          onSave={handleEditAsiakasSave}
          onCancel={handleEditAsiakasCancel}
          ownerAsiakasId={ownerAsiakasId}
        />
      </MyDialog>
      <Autocomplete
        autoHighlight
        selectOnFocus
        id="asiakasmain-autocomplete"
        sx={{
          width: "100%",
          "& .MuiInputBase-input": {
            height: height !== undefined ? height : "auto",
            marginBottom: "2px",
          },
        }}
        options={sortedAsiakasList()}
        value={getAsiakas(selectedAsiakasId)}
        getOptionLabel={(option) => option?.asiakasNimi || ""}
        onChange={(event, newValue) => handleAutoCompleteSelect(newValue)}
        onInputChange={(event, newValue) => onInputChange(newValue)}
        onOpen={onOpen}
        onClose={onClose}
        disabled={isLoading}
        onBlur={onBlur}
        renderOption={(props, option) => (
          <Box {...props} key={option.asiakasId}>
            <div>
              <Typography variant="body" noWrap color="primary">
                {option.asiakasNimi}
              </Typography>
              <Typography variant="body2" noWrap color="primary">
                {option.ytunnus}
              </Typography>
            </div>
          </Box>
        )}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              variant={variant}
              fullWidth
              slotProps={{
                inputLabel: { shrink: shrinkLabel === false ? false : true },
                htmlInput: { ...params.inputProps, autoComplete: "off" },
                input: {
                  ...params.InputProps,
                  autoComplete: "off",
                  endAdornment: (
                    <>
                      {isLoading && <ProgressCirc />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          );
        }}
        filterOptions={(options, params) => {
          let newOptions = [];
          const val = params.inputValue.toLowerCase();
          if (val.length > 0)
            options.forEach((option) => {
              if (newOptions.length > 15) return;
              if (
                (option.asiakasNimi && option.asiakasNimi.toLowerCase().indexOf(val) > -1) ||
                (option.kommentti && option.kommentti.toLowerCase().indexOf(val) > -1) ||
                (option.ytunnus && option.ytunnus.toLowerCase().indexOf(val) > -1)
              ) {
                newOptions.push(option);
              }
            });

          // if (newOptions.length === 0) {
          if (params.inputValue.length > 2)
            newOptions.push({
              asiakasId: 0,
              asiakasNimi: "Luo uusi asiakas nimellä: ",
              ytunnus: params.inputValue,
            });
          // }
          return newOptions;
        }}
      />
    </>
  );
}

AsiakasSelectorAutocomplete.propTypes = {
  defaultAsiakasId: PropTypes.number,
  setAsiakas: PropTypes.func,
  filter: PropTypes.string,
  variant: PropTypes.string,
  setIsEditing: PropTypes.func,
  label: PropTypes.string,
  height: PropTypes.number,
  shrinkLabel: PropTypes.bool,
  onBlur: PropTypes.func,
  ownerAsiakasId: PropTypes.number,
};
